import { AlertTriangle, Trash2, Wrench } from "lucide-react";
import { useState } from "react";

import { clearBankBookings } from "@/apis/developer-tools";
import type { Transaction } from "@/apis/gql/types";
import { SuperAdminOnly } from "@/components/shared/miscellaneous/superadmin-only";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { LoadingSpinner } from "@/components/ui/spinner";

export const SuperAdminTools = ({
  transactions,
  refetch,
}: { transactions: Transaction[]; refetch: () => void }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <SuperAdminOnly>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="border border-amber-200 bg-amber-50 rounded-lg overflow-hidden shadow-sm">
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center justify-between px-4 py-2 hover:bg-amber-100 transition-colors cursor-pointer">
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-900">
                  Super Admin Tools
                </span>
              </div>
              <div className="flex items-center gap-2">
                {isOpen ? (
                  <span className="text-xs text-amber-600">Hide</span>
                ) : (
                  <span className="text-xs text-amber-600">Show tools</span>
                )}
              </div>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="border-t border-amber-200 bg-white px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                </div>
                <div className="flex-1 space-y-3">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-1">
                      Danger Zone
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      These actions are irreversible and should only be used for
                      development and testing purposes.
                    </p>
                  </div>
                  <div className="pt-1">
                    <ClearBookingsButton
                      transactions={transactions}
                      refetch={refetch}
                    />
                  </div>
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </SuperAdminOnly>
  );
};

const ClearBookingsButton = ({
  transactions,
  refetch,
}: { transactions: Transaction[]; refetch: () => void }) => {
  const [loading, setLoading] = useState(false);

  const handleClearBookings = async () => {
    setLoading(true);
    try {
      await Promise.all(
        transactions.map(async (transaction) => {
          await clearBankBookings(transaction.bankTxnId);
        }),
      );
      refetch();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      className="border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400 transition-colors"
      onClick={handleClearBookings}
      disabled={loading}
    >
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          Clear All Bookings
        </>
      )}
    </Button>
  );
};
